import { useEffect, useRef, useState } from "react";
import Box from "elements/Box";
import { Skeleton } from "@mui/material";
import { ApiRequest } from "GlobalFunctions";
import { BsFiletypePdf } from "react-icons/bs";
import { PiCertificateBold } from "react-icons/pi";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

function PdfPreview({ src }: { src: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);

    const render = async () => {
      try {
        const pdf = await (pdfjsLib as any).getDocument({ url: src, withCredentials: false }).promise;
        if (cancelled) return;
        const page = await pdf.getPage(1);
        if (cancelled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const containerWidth = canvas.parentElement?.clientWidth || 155;
        const viewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / viewport.width;
        const scaled = page.getViewport({ scale });

        canvas.width = scaled.width;
        canvas.height = scaled.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        await page.render({ canvasContext: ctx, viewport: scaled }).promise;
      } catch (e) {
        console.error("[PdfPreview] error loading", src, e);
        if (!cancelled) setError(true);
      }
    };

    render();
    return () => { cancelled = true; };
  }, [src]);

  if (error) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 6 }}>
        <BsFiletypePdf size={44} color="#005F6A" style={{ opacity: 0.7 }} />
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "#005F6A", opacity: 0.6, textTransform: "uppercase" }}>PDF</span>
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
    />
  );
}

export default function CredentialsGallery({ nurse_id }: { nurse_id: any }) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!nurse_id) return;
    setLoading(true);
    await ApiRequest({
      url: "/kinesiologists/credentials",
      method: "GET",
      query: { nurse_id, visible_only: 1 },
      setResponse: (r: any) => {
        setLoading(false);
        if (r?.status === "success") setItems(r.items || []);
        else setItems([]);
      },
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nurse_id]);

  if (loading) return <Skeleton variant="rounded" height={180} sx={{ mt: 2 }} />;
  if (!items || items.length === 0) return null;

  const isPdf = (url: string) => url?.toLowerCase().endsWith(".pdf");

  return (
    <Box className="intent-box">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
        <PiCertificateBold size={20} color="#005F6A" />
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#005F6A" }}>
          Certificados y Especializaciones
        </h3>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))",
        gap: 16,
      }}>
        {items.map((it: any) => {
          const src = it.image || it.image_url;
          const pdf = isPdf(src);
          return (
            <a
              key={it.credential_id}
              href={src}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none" }}
            >
              <div
                style={{
                  borderRadius: 14,
                  overflow: "hidden",
                  background: "#fff",
                  boxShadow: "0 2px 14px rgba(0,0,0,0.08)",
                  border: "1px solid #e8edf4",
                  transition: "transform 0.18s, box-shadow 0.18s",
                  cursor: "pointer",
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = "translateY(-3px)";
                  (e.currentTarget as HTMLDivElement).style.boxShadow = "0 8px 24px rgba(19,80,149,0.14)";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
                  (e.currentTarget as HTMLDivElement).style.boxShadow = "0 2px 14px rgba(0,0,0,0.08)";
                }}
              >
                {/* Thumbnail */}
                <div style={{
                  height: 160,
                  background: pdf ? "#f8fafd" : "#f5f5f5",
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  {pdf ? (
                    <>
                      <PdfPreview src={src} />
                      <div style={{
                        position: "absolute",
                        bottom: 6,
                        right: 8,
                        background: "rgba(19,80,149,0.85)",
                        borderRadius: 20,
                        padding: "2px 8px",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}>
                        <BsFiletypePdf size={11} color="#fff" />
                        <span style={{ fontSize: 10, color: "#fff", fontWeight: 700, letterSpacing: 0.5 }}>PDF</span>
                      </div>
                    </>
                  ) : (
                    <img
                      src={src}
                      alt={it.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  )}
                </div>

                {/* Footer */}
                <div style={{
                  padding: "10px 14px 12px",
                  borderTop: "1px solid #f0f4f8",
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#1e293b",
                    lineHeight: 1.35,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}>
                    {it.title}
                  </p>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </Box>
  );
}
